import { useContext } from "react";
import { ConfigContext } from "../../util/ConfigContext";
import { useQuery } from "@tanstack/react-query";
import { DynamicForm } from "../../util/DynamicForm";
import { useGeneratedForm } from "../../hooks/useGeneratedForm";
import { apiPost, apiGet } from "../../util/axiosUtil";
import API_URL from "../../config/apiConfig";
import { z } from "zod";
import MutationHandler from "../../util/MutationHandler";

export default function CuttingMeetingForm({ onCancel, onSuccess }) {
  const { jwt } = useContext(ConfigContext);
  const isVideoTenant = jwt.iv;
  const CREATE_MEETING_URL = `${API_URL}/api/meetings`;
  const FETCH_ZOOMS_URL = `${API_URL}/api/zooms`;

  const { data: zoomData, isLoading: zoomLoading } = useQuery({
    queryKey: ["zoomAccounts"],
    queryFn: async () => {
      const res = await apiGet(FETCH_ZOOMS_URL);
      return res.data; // Assuming res.data has .content inside
    },
  });

  const formConfig = {
    name: {
      label: "Name",
      type: "text",
      placeholder: "Enter your name",
      defaultValue: "",
      required: true,
      validation: z.string().superRefine((value, ctx) => {
        if (!value.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Name is required",
          });
        } else if (value.length < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Name should be more than 1 letters",
          });
        }
      }),
    },
    email: {
      label: "Email",
      type: "email",
      placeholder: "Enter your Email",
      defaultValue: "",
      fieldType: ["Audio"],
      required: true,
      validation: z
        .string()
        .optional()
        .superRefine((value, ctx) => {
          if (value && value.trim()) {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Invalid email format",
              });
            }
          }
        }),
    },
    meetingNumber: {
      label: "Meeting Number",
      type: "text",
      placeholder: "Enter Meeting Number",
      required: true,
      defaultValue: "",
      normalize: (val) => val.replace(/\s+/g, ""),
      validation: z.string().superRefine((value, ctx) => {
        if (!value.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Meeting Number is required",
          });
        } else if (value.length < 4) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Name should be more than 3 letters",
          });
        }
      }),
    },
    meetingPassword: {
      label: "Meeting Password",
      type: "password",
      placeholder: "Enter Meeting Password",
      defaultValue: "",
      required: true,
      normalize: (val) => val.replace(/\s+/g, ""),
      validation: z.string().min(4, "Password must be at least 4 characters"),
    },
    zoomId: {
      label: "Zoom Account",
      type: "select",
      valueType: "number",
      options: zoomData
        ? zoomData.data.map((zoom) => ({
            id: zoom.id,
            name: zoom.email,
          }))
        : [],
      defaultValue: null,
      required: true,
      validation: z.preprocess(
        (val) =>
          val === "" || val === null || val === undefined ? null : Number(val),
        z
          .number({ invalid_type_error: "Zoom is required" })
          .min(1, "Zoom is required"),
      ),
    },
    // password: {
    //   label: "Password",
    //   type: "password",
    //   placeholder: "Enter password",
    //   defaultValue: "",
    //   required: false,
    //   validation: z
    //     .string()
    //     .optional()
    //     .superRefine((value, ctx) => {
    //       if (value && value.trim().length > 0 && value.length < 4) {
    //         ctx.addIssue({
    //           code: z.ZodIssueCode.too_small,
    //           minimum: 4,
    //           type: "string",
    //           inclusive: true,
    //           message: "Password must be at least 4 characters",
    //         });
    //       }
    //     }),
    // },
  };

  // 🔹 Filter fields based on tenant type
  const filteredFormConfig = Object.fromEntries(
    Object.entries(formConfig).filter(([_, config]) => {
      if (!config.fieldType || config.fieldType.length === 0) return true;
      if (isVideoTenant && config.fieldType.includes("Video")) return true;
      if (!isVideoTenant && config.fieldType.includes("Audio")) return true;
      return false;
    }),
  );

  return (
    <MutationHandler
      mutationFn={(formData) => {
        return apiPost(CREATE_MEETING_URL, formData);
      }}
      onCancel={onCancel}
      onSuccess={onSuccess}
    >
      {(mutation) => (
        <CuttingMeeting
          formConfig={filteredFormConfig}
          onSubmit={(value) => mutation.mutate(value)}
          isLoading={mutation.isPending}
          onCancel={onCancel}
        />
      )}
    </MutationHandler>
  );
}
function CuttingMeeting({ formConfig, onSubmit, isLoading, onCancel }) {
  const form = useGeneratedForm(formConfig, async ({ value }) => {
    onSubmit(value);
  });

  return (
    <DynamicForm
      form={form}
      formConfig={formConfig}
      isLoading={isLoading}
      onCancel={onCancel}
      buttonText={"Create Meeting"}
    />
  );
}
